import React, { useCallback, useState } from 'react';

import { useGraphQLMutation } from '@betterment/graphql';
import { Button, Dialog, H1, HStack, Text } from '@betterment/samba-react';

import { SendClientSecuritiesLendingInvitationDocument } from './graphql/mutation.sendClientSecuritiesLendingInvitation.generated';

interface SecuritiesLendingInviteDialogProps {
  memberId: string | null;
  memberName: string | undefined;
  onClose: () => void;
  onSuccess: () => void;
}

export const SecuritiesLendingInviteDialog: React.FC<SecuritiesLendingInviteDialogProps> = ({
  memberId,
  memberName,
  onClose,
  onSuccess,
}) => {
  const [sendClientSecuritiesLendingInvitation, { loading }] = useGraphQLMutation(
    SendClientSecuritiesLendingInvitationDocument
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSendInvite = useCallback(async () => {
    if (!memberId) return;
    setErrorMessage(null);
    const result = await sendClientSecuritiesLendingInvitation({ variables: { input: { clientId: memberId } } });
    if (result.data?.sendClientSecuritiesLendingInvitation.displayableErrors.length === 0) {
      onSuccess();
    } else {
      setErrorMessage('Invite couldn’t be sent. Try again.');
    }
  }, [memberId, onSuccess, sendClientSecuritiesLendingInvitation, setErrorMessage]);

  return (
    <Dialog
      open={memberId !== null}
      overlay="default"
      onOpenChange={open => {
        if (!open) onClose();
      }}
    >
      <Dialog.Content size="large" css={{ display: 'flex', flexDirection: 'column', gap: 'space.150', p: 'space.400' }}>
        <Dialog.Title asChild={true}>
          <H1 variant="title300">Invite {memberName ?? 'this client'} to enable securities lending.</H1>
        </Dialog.Title>
        <Dialog.Description asChild={true}>
          <Text variant="text100" css={{ mb: 'space.50' }}>
            Your client will receive an invite via email. They will need to confirm opt in before securities lending is
            turned on for their accounts.
          </Text>
        </Dialog.Description>
        <HStack css={{ mt: 'space.200', justifyContent: 'flex-start' }}>
          <Button
            variant="solidPrimary"
            onClick={() => {
              void handleSendInvite();
            }}
            loading={loading}
          >
            Send invite
          </Button>
        </HStack>
        {errorMessage && <Text color="text.critical">{errorMessage}</Text>}
      </Dialog.Content>
    </Dialog>
  );
};
